import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "../../../config/axiosConfig";

// Estado 3 (Asignado) de una OT de Venta, solo para clientes AVL con
// cliente_servicio.ot_vehi=1 (mayoría de los clientes): espejo de ingreso_placa.php
// + guarda_placa.php + guarda_seleccion.php — hay que tener al menos una placa
// registrada para este servicio antes de poder activar los módems vendidos.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };

const IngresoPlacaOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, onPlacasCambiadas }) => {
  const [disponibles, setDisponibles] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [numero, setNumero] = useState("");
  const [letra, setLetra] = useState("");
  const [registrando, setRegistrando] = useState(false);

  const [elegidas, setElegidas] = useState([]);
  const [seleccionando, setSeleccionando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([
      axios.get(`/api/ot-venta-placa/disponibles/${cod_servicio_cliente}`),
      axios.get(`/api/ot-venta-placa/seleccionadas/${cod_ot}`),
    ])
      .then(([resDisp, resSel]) => {
        setDisponibles(resDisp.data || []);
        const sel = resSel.data || [];
        setSeleccionadas(sel);
        if (onPlacasCambiadas) onPlacasCambiadas(sel.length);
      })
      .catch(() => setError("No se pudieron cargar las placas."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cod_ot, cod_servicio_cliente]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleRegistrar = async () => {
    if (!numero.trim() || !letra.trim()) return;
    setRegistrando(true);
    setError("");
    try {
      await axios.post("/api/ot-venta-placa/registrar", {
        cod_ot,
        cod_cliente,
        cod_servicio_cliente,
        placa: `${numero.trim().toUpperCase()}-${letra.trim().toUpperCase()}`,
      });
      setNumero("");
      setLetra("");
      cargar();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo registrar la placa.");
    } finally {
      setRegistrando(false);
    }
  };

  const toggleElegida = (placa) => {
    setElegidas((prev) => (prev.includes(placa) ? prev.filter((p) => p !== placa) : [...prev, placa]));
  };

  const handleSeleccionar = async () => {
    if (!elegidas.length) return;
    setSeleccionando(true);
    setError("");
    try {
      await axios.post("/api/ot-venta-placa/seleccionar", { cod_ot, cod_cliente, placas: elegidas });
      setElegidas([]);
      cargar();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudieron seleccionar las placas.");
    } finally {
      setSeleccionando(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {!seleccionadas.length && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Debe registrar o seleccionar al menos una placa para continuar con la activación de los módems.
        </Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Registrar placa nueva</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TextField size="small" label="Número" value={numero} onChange={(e) => setNumero(e.target.value.slice(0, 4))} sx={{ width: 100 }} />
            <Typography>-</Typography>
            <TextField size="small" label="Letra" value={letra} onChange={(e) => setLetra(e.target.value.slice(0, 4))} sx={{ width: 100 }} />
            <Button size="small" variant="contained" onClick={handleRegistrar} disabled={registrando || !numero.trim() || !letra.trim()}>
              {registrando ? <CircularProgress size={16} color="inherit" /> : "Registrar"}
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Placas ya registradas para este servicio</Typography>
          <TableContainer sx={{ maxHeight: 240 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Placa</TableCell>
                  <TableCell sx={headCellSx}>Módems</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!disponibles.length ? (
                  <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Sin placas registradas todavía para este servicio.</Typography></TableCell></TableRow>
                ) : disponibles.map((d) => (
                  <TableRow key={d.placa} hover>
                    <TableCell><Checkbox size="small" checked={elegidas.includes(d.placa)} onChange={() => toggleElegida(d.placa)} sx={{ p: 0 }} /></TableCell>
                    <TableCell>{d.placa}</TableCell>
                    <TableCell>{d.modems}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button size="small" variant="outlined" onClick={handleSeleccionar} disabled={!elegidas.length || seleccionando}>
              {seleccionando ? <CircularProgress size={16} /> : "Seleccionar"}
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Placas seleccionadas para esta OT</Typography>
            <Chip size="small" color="success" label={seleccionadas.length} sx={{ height: 20, fontSize: 11 }} />
          </Stack>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow><TableCell sx={headCellSx}>Placa</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {!seleccionadas.length ? (
                  <TableRow><TableCell><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Ninguna todavía.</Typography></TableCell></TableRow>
                ) : seleccionadas.map((s) => (
                  <TableRow key={s.placa} hover><TableCell>{s.placa}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
};

export default IngresoPlacaOT;
